import { useCallback, useEffect, useRef, useState } from "react";
import { createTextRegistryAdminApi } from "../../../services/textRegistryAdminApi";
import { normalizeStationsTextRegistry } from "../../../services/StationsTextConfigService";
import { applyFindReplace, filterEntries, matchesText } from "./selectors";
import type {
	DrawerTab,
	FilterState,
	FindReplaceState,
	GlossaryTerm,
	PaginationState,
	SortState,
	StationsTextEntry,
	StationsTextRegistryAdminPayload,
	TextRegistryScope,
	TextWorkspaceMutations,
	TextWorkspaceState,
} from "./types";

const DEFAULT_FILTERS: FilterState = {
	search: "",
	screen: "",
	group: "",
	tag: "",
	status: "",
	translationLang: "vi",
	translationState: "",
};
const DEFAULT_SORT: SortState = { column: "key", dir: "asc" };
const DEFAULT_PAGINATION: PaginationState = { page: 0, pageSize: 50 };
const DEFAULT_FIND_REPLACE: FindReplaceState = {
	open: false,
	find: "",
	replace: "",
	scope: "all",
	matchMode: "substring",
};

export function useTextWorkspace(initialScope: TextRegistryScope): TextWorkspaceState & TextWorkspaceMutations {
	const [scope, setScope] = useState<TextRegistryScope>(initialScope);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [publishing, setPublishing] = useState(false);
	const [payload, setPayload] = useState<StationsTextRegistryAdminPayload | null>(null);
	const [draftDirty, setDraftDirty] = useState(false);
	const [detections, setDetections] = useState<TextWorkspaceState["detections"]>([]);
	const [glossary, setGlossary] = useState<GlossaryTerm[]>([]);
	const [filters, setFiltersState] = useState<FilterState>(DEFAULT_FILTERS);
	const [sort, setSortState] = useState<SortState>(DEFAULT_SORT);
	const [pagination, setPaginationState] = useState<PaginationState>(DEFAULT_PAGINATION);
	const [selection, setSelection] = useState<Set<string>>(new Set());
	const [activeKey, setActiveKeyState] = useState<string | null>(null);
	const [drawerTab, setDrawerTabState] = useState<DrawerTab>("style");
	const [findReplace, setFindReplaceState] = useState<FindReplaceState>(DEFAULT_FIND_REPLACE);
	const [feedback, setFeedback] = useState<TextWorkspaceState["feedback"]>(null);
	const [detectionPanelOpen, setDetectionPanelOpenState] = useState(false);
	const [glossaryPanelOpen, setGlossaryPanelOpenState] = useState(false);

	const apiRef = useRef(createTextRegistryAdminApi(scope));

	useEffect(() => {
		apiRef.current = createTextRegistryAdminApi(scope);
		setFiltersState(DEFAULT_FILTERS);
		setPaginationState(DEFAULT_PAGINATION);
		setSelection(new Set());
		setActiveKeyState(null);
		void loadAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scope]);

	async function loadAll() {
		setLoading(true);
		try {
			const api = apiRef.current;
			const [p, d, g] = await Promise.all([api.getRegistryAdmin(), api.getDetections(), api.listGlossary()]);
			setPayload(p);
			setDetections(d);
			setGlossary(g);
			setDraftDirty(false);
		} catch {
			setFeedback({ message: "Failed to load workspace data.", kind: "error" });
		} finally {
			setLoading(false);
		}
	}

	function showFeedback(message: string, kind: "success" | "error") {
		setFeedback({ message, kind });
		setTimeout(() => setFeedback(null), 4000);
	}

	const updateDraftItem = useCallback((key: string, patch: Partial<StationsTextEntry>) => {
		setPayload((prev) => {
			if (!prev) return prev;
			const items = prev.draft.items.map((item) => (item.key === key ? { ...item, ...patch } : item));
			return { ...prev, draft: { ...prev.draft, items } };
		});
		setDraftDirty(true);
	}, []);

	const toggleSelect = useCallback((key: string) => {
		setSelection((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const selectAll = useCallback(() => {
		setPayload((prev) => {
			if (!prev) return prev;
			setSelection(new Set(prev.draft.items.map((i) => i.key)));
			return prev;
		});
	}, []);

	const selectNone = useCallback(() => setSelection(new Set()), []);

	const applyBulkStyle = useCallback(
		(stylePatch: Partial<StationsTextEntry["stylePreset"] & { colorToken: string }>) => {
			setPayload((prev) => {
				if (!prev) return prev;
				const items = prev.draft.items.map((item) => {
					if (!selection.has(item.key)) return item;
					const { colorToken, ...styleFields } = stylePatch as any;
					return {
						...item,
						stylePreset: { ...item.stylePreset, ...styleFields },
						...(colorToken !== undefined ? { colorToken } : {}),
					};
				});
				return { ...prev, draft: { ...prev.draft, items } };
			});
			setDraftDirty(true);
		},
		[selection]
	);

	const applyBulkTag = useCallback(
		(tag: string, mode: "add" | "remove") => {
			setPayload((prev) => {
				if (!prev) return prev;
				const items = prev.draft.items.map((item) => {
					if (!selection.has(item.key)) return item;
					const existing: string[] = (item as any).tags || [];
					const updated =
						mode === "add"
							? Array.from(new Set([...existing, tag])).slice(0, 8)
							: existing.filter((t) => t !== tag);
					return { ...item, tags: updated };
				});
				return { ...prev, draft: { ...prev.draft, items } };
			});
			setDraftDirty(true);
		},
		[selection]
	);

	const publishSelectedOnly = useCallback(async () => {
		if (selection.size === 0) return;
		setPublishing(true);
		try {
			await apiRef.current.publishSelectedOnly(Array.from(selection));
			showFeedback("Selected keys published.", "success");
			setSelection(new Set());
			await loadAll();
		} catch {
			showFeedback("Failed to publish selected keys.", "error");
		} finally {
			setPublishing(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selection]);

	const runFindReplace = useCallback(
		(params: {
			find: string;
			replace: string;
			scope: FindReplaceState["scope"];
			matchMode: FindReplaceState["matchMode"];
		}): number => {
			if (!payload || !params.find) return 0;
			let candidates = payload.draft.items;
			if (params.scope === "screen" && filters.screen) {
				candidates = candidates.filter((e) => e.screen === filters.screen);
			} else if (params.scope === "group" && filters.group) {
				candidates = candidates.filter((e) => e.group === filters.group);
			} else if (params.scope === "selected") {
				candidates = candidates.filter((e) => selection.has(e.key));
			}

			let count = 0;
			setPayload((prev) => {
				if (!prev) return prev;
				const items = prev.draft.items.map((item) => {
					if (!candidates.find((c) => c.key === item.key)) return item;
					// Effective text = currentText for overridden rows, defaultText for DEFAULT rows.
					// Replace runs on the effective text; result becomes currentText so DEFAULT rows
					// get promoted to overrides as a side effect of the replace.
					const sourceText = item.currentText || item.defaultText;
					if (!matchesText(sourceText, params.find, params.matchMode)) return item;
					count++;
					return {
						...item,
						currentText: applyFindReplace(sourceText, params.find, params.replace, params.matchMode),
					};
				});
				return { ...prev, draft: { ...prev.draft, items } };
			});
			setDraftDirty(true);
			return count;
		},
		[payload, filters, selection]
	);

	const mapDetection = useCallback(
		(detection: TextWorkspaceState["detections"][0], meta: { screen: string; group: string; tags?: string[] }) => {
			setPayload((prev) => {
				if (!prev) return prev;
				const newEntry: StationsTextEntry & { tags: string[] } = {
					key: `detected.${detection.fingerprint.slice(0, 12)}`,
					screen: meta.screen,
					group: meta.group,
					defaultText: detection.text,
					currentText: detection.text,
					translations: {},
					stylePreset: { fontSizePreset: "md", fontWeight: "normal", fontStyle: "normal" },
					colorToken: "default",
					status: "draft",
					updatedAt: null,
					tags: meta.tags || [],
				};
				const existing = prev.draft.items.find((i) => i.key === newEntry.key);
				const items = existing
					? prev.draft.items.map((i) => (i.key === newEntry.key ? newEntry : i))
					: [...prev.draft.items, newEntry];
				return { ...prev, draft: { ...prev.draft, items } };
			});
			setDetections((prev) =>
				prev.map((d) => (d.fingerprint === detection.fingerprint ? { ...d, status: "resolved" as const } : d))
			);
			setDraftDirty(true);
		},
		[]
	);

	const ignoreDetection = useCallback(async (fingerprint: string) => {
		try {
			const updated = await apiRef.current.ignoreDetection(fingerprint);
			setDetections(updated);
		} catch {
			showFeedback("Failed to ignore detection.", "error");
		}
	}, []);

	const saveDraft = useCallback(async () => {
		if (!payload) return;
		setSaving(true);
		try {
			const updated = await apiRef.current.saveDraft(payload.draft);
			setPayload((prev) => (prev ? { ...prev, draft: updated } : prev));
			setDraftDirty(false);
			showFeedback("Draft saved successfully.", "success");
		} catch {
			showFeedback("Failed to save draft.", "error");
		} finally {
			setSaving(false);
		}
	}, [payload]);

	const publish = useCallback(async () => {
		setPublishing(true);
		try {
			const updated = await apiRef.current.publish();
			setPayload((prev) => (prev ? { ...prev, published: updated } : prev));
			setDraftDirty(false);
			showFeedback("Published successfully.", "success");
			const newDetections = await apiRef.current.getDetections();
			setDetections(newDetections);
		} catch {
			showFeedback("Failed to publish.", "error");
		} finally {
			setPublishing(false);
		}
	}, []);

	const revertKeyToVersion = useCallback(async (key: string, versionId: number) => {
		try {
			await apiRef.current.revertKeyToVersion(key, versionId);
			const updated = await apiRef.current.getRegistryAdmin();
			setPayload(updated);
			setDraftDirty(true);
			showFeedback("Entry reverted to selected version.", "success");
		} catch {
			showFeedback("Failed to revert entry.", "error");
		}
	}, []);

	const upsertGlossary = useCallback(async (term: Omit<GlossaryTerm, "id" | "scope">) => {
		try {
			const created = await apiRef.current.upsertGlossary(term);
			setGlossary((prev) => {
				const idx = prev.findIndex((g) => g.id === created.id);
				return idx >= 0 ? prev.map((g, i) => (i === idx ? created : g)) : [...prev, created];
			});
		} catch {
			showFeedback("Failed to save glossary term.", "error");
		}
	}, []);

	const removeGlossary = useCallback(async (id: number) => {
		try {
			await apiRef.current.deleteGlossary(id);
			setGlossary((prev) => prev.filter((g) => g.id !== id));
		} catch {
			showFeedback("Failed to delete glossary term.", "error");
		}
	}, []);

	const setFilters = useCallback((patch: Partial<FilterState>) => {
		setFiltersState((prev) => ({ ...prev, ...patch }));
		setPaginationState((prev) => ({ ...prev, page: 0 }));
	}, []);

	const setSort = useCallback((s: SortState) => setSortState(s), []);

	const setPagination = useCallback((patch: Partial<PaginationState>) => {
		setPaginationState((prev) => ({ ...prev, ...patch }));
	}, []);

	const setActiveKey = useCallback((key: string | null) => setActiveKeyState(key), []);
	const setDrawerTab = useCallback((tab: DrawerTab) => setDrawerTabState(tab), []);

	const setFindReplace = useCallback((patch: Partial<FindReplaceState>) => {
		setFindReplaceState((prev) => ({ ...prev, ...patch }));
	}, []);

	const clearFeedback = useCallback(() => setFeedback(null), []);

	return {
		scope,
		loading,
		saving,
		publishing,
		payload,
		draftDirty,
		detections,
		glossary,
		filters,
		sort,
		pagination,
		selection,
		activeKey,
		drawerTab,
		findReplace,
		feedback,
		detectionPanelOpen,
		glossaryPanelOpen,
		updateDraftItem,
		toggleSelect,
		selectAll,
		selectNone,
		applyBulkStyle,
		applyBulkTag,
		publishSelectedOnly,
		runFindReplace,
		mapDetection,
		ignoreDetection,
		saveDraft,
		publish,
		revertKeyToVersion,
		upsertGlossary,
		removeGlossary,
		setScope,
		setFilters,
		setSort,
		setPagination,
		setActiveKey,
		setDrawerTab,
		setFindReplace,
		setDetectionPanelOpen: setDetectionPanelOpenState,
		setGlossaryPanelOpen: setGlossaryPanelOpenState,
		clearFeedback,
		refresh: loadAll,
	};
}
